import {
  Button,
  ChakraSelect,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Stack,
  useBoolean,
  useDisclosure,
} from '@elkaso-app/web-design';
import { BaseFilterButton } from 'components/base-table/filters';
import { usePageParams } from 'hooks/use-page-params';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

type TPlatformOption = {
  value: string;
  label: string;
};

type TInputs = {
  name: string;
  sourcePlatform: TPlatformOption | undefined;
};

const defaultValues: TInputs = {
  name: '',
  sourcePlatform: undefined,
};

// Common source platforms
const platformOptions: TPlatformOption[] = [
  { value: 'google', label: 'Google' },
  { value: 'talabat', label: 'Talabat' },
  { value: 'zomato', label: 'Zomato' },
];

export const CombinedLocationFilterButton = () => {
  const [isActive, setIsActive] = useBoolean(false);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();

  const { register, control, handleSubmit, formState, reset, setValue } = useForm<TInputs>({
    defaultValues: { ...defaultValues },
  });

  // Update active filter button
  useEffect(() => {
    if (params.name || params.sourcePlatform) setIsActive.on();
    else setIsActive.off();
  }, [params.name, params.sourcePlatform, setIsActive]);

  // Update values when params change
  useEffect(() => {
    setValue('name', (params.name as string) ?? '');
    setValue('sourcePlatform', platformOptions.find((option) => option.value === params.sourcePlatform) ?? undefined);
  }, [params.name, params.sourcePlatform, setValue]);

  const onSubmit = (data: TInputs) => {
    // Handle location name
    if (data.name.trim()) {
      params.name = data.name.trim();
    } else {
      delete params.name;
    }

    // Handle platform
    if (data.sourcePlatform?.value) {
      params.sourcePlatform = data.sourcePlatform.value;
    } else {
      delete params.sourcePlatform;
    }

    params.page = 1; // Reset to first page
    setPageParams(params);
    onClose();
    reset(data);
  };

  const handleClose = () => {
    onClose();
    // Reset form to current param values
    setValue('name', (params.name as string) ?? '');
    setValue('sourcePlatform', platformOptions.find((option) => option.value === params.sourcePlatform) ?? undefined);
  };

  const handleDisableFilterButton = () => {
    const isDisabled = Object.keys(formState.dirtyFields).length ? false : true;
    return isDisabled;
  };

  return (
    <BaseFilterButton
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={handleClose}
      isActive={isActive}
      formComponent={
        <Stack
          as='form'
          spacing='lg'
          p='md'
          pt='lg'
          onSubmit={handleSubmit(onSubmit)}
          onClick={(e) => e.stopPropagation()}>
          <FormControl>
            <FormLabel>Location Name</FormLabel>
            <Input size='sm' placeholder='Search by location name...' {...register('name')} />
          </FormControl>

          <Controller
            control={control}
            name='sourcePlatform'
            render={({ field: { onChange, name, onBlur, value } }) => (
              <FormControl>
                <FormLabel>Source Platform</FormLabel>
                <ChakraSelect
                  id='sourcePlatform'
                  options={platformOptions}
                  onChange={(selectedOption) => {
                    onChange(selectedOption);
                  }}
                  name={name}
                  onBlur={onBlur}
                  value={value}
                  placeholder='Select platform...'
                  isClearable
                  getOptionLabel={(option: any) => option.label}
                  getOptionValue={(option: any) => option.value}
                  closeMenuOnSelect={true}
                  blurInputOnSelect={false}
                  menuPlacement='auto'
                />
              </FormControl>
            )}
          />

          <HStack justify='flex-end'>
            <Button variant='outline' colorScheme='red' onClick={handleClose}>
              Cancel
            </Button>
            <Button variant='solid' colorScheme='red' type='submit' disabled={handleDisableFilterButton()}>
              Filter
            </Button>
          </HStack>
        </Stack>
      }
    />
  );
};
